import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const { login, verifyOTP, resendOTP, selectMobile } = useAuth();
  const navigate = useNavigate();

  // OTP state
  const [otpMode, setOtpMode] = useState(false);
  const [otpToken, setOtpToken] = useState('');
  const [otpMobile, setOtpMobile] = useState('');
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [otpTimer, setOtpTimer] = useState(0);
  const [resending, setResending] = useState(false);
  const otpRefs = [React.useRef(), React.useRef(), React.useRef(), React.useRef(), React.useRef(), React.useRef()];

  // Mobile selection state
  const [mobileSelectMode, setMobileSelectMode] = useState(false);
  const [availableMobiles, setAvailableMobiles] = useState([]);

  // OTP countdown timer
  useEffect(() => {
    if (otpTimer <= 0) return;
    const t = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
    return () => clearTimeout(t);
  }, [otpTimer]);

  // Auto-focus first OTP input when entering OTP mode
  useEffect(() => {
    if (otpMode && otpRefs[0]?.current) {
      setTimeout(() => otpRefs[0].current?.focus(), 300);
    }
  }, [otpMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(username, password);
    
    if (result.success) {
      navigate('/');
    } else if (result.otp_required) {
      setOtpToken(result.otp_token);
      if (result.mobile_selection && result.mobiles?.length > 1) {
        // Multiple mobiles — show selection screen
        setMobileSelectMode(true);
        setAvailableMobiles(result.mobiles);
      } else {
        // Single mobile — go straight to OTP input
        setOtpMode(true);
        setOtpMobile(result.mobile);
        setOtpTimer(300);
        setOtpDigits(['', '', '', '', '', '']);
      }
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;
    const newDigits = [...otpDigits];
    newDigits[index] = value;
    setOtpDigits(newDigits);
    // Auto-focus next input
    if (value && index < 5) {
      otpRefs[index + 1]?.current?.focus();
    }
    // Auto-submit when all 6 digits entered
    if (value && index === 5 && newDigits.every(d => d !== '')) {
      handleOtpSubmit(newDigits.join(''));
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpRefs[index - 1]?.current?.focus();
    }
    if (e.key === 'Enter') {
      const code = otpDigits.join('');
      if (code.length === 6) handleOtpSubmit(code);
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const newDigits = pasted.split('');
      setOtpDigits(newDigits);
      otpRefs[5]?.current?.focus();
      setTimeout(() => handleOtpSubmit(pasted), 100);
    }
  };

  const handleOtpSubmit = async (code) => {
    if (!code || code.length !== 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }
    setError('');
    setLoading(true);
    const result = await verifyOTP(otpToken, code);
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
      setOtpDigits(['', '', '', '', '', '']);
      otpRefs[0]?.current?.focus();
    }
    setLoading(false);
  };

  const handleResendOtp = async () => {
    setResending(true);
    setError('');
    const result = await resendOTP(otpToken);
    if (result.success) {
      setOtpTimer(300);
      setOtpDigits(['', '', '', '', '', '']);
      otpRefs[0]?.current?.focus();
    } else {
      setError(result.error);
    }
    setResending(false);
  };

  const handleMobileSelect = async (mobile) => {
    setError('');
    setLoading(true);
    const result = await selectMobile(otpToken, mobile);
    if (result.success) {
      setMobileSelectMode(false);
      setOtpMode(true);
      setOtpMobile(result.mobile);
      setOtpTimer(300);
      setOtpDigits(['', '', '', '', '', '']);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleBackToLogin = () => {
    setOtpMode(false);
    setMobileSelectMode(false);
    setOtpToken('');
    setOtpMobile('');
    setAvailableMobiles([]);
    setOtpDigits(['', '', '', '', '', '']);
    setError('');
  };

  const formatTimer = (s) => `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`;

  // Animated stats counter
  const [stats, setStats] = useState({ messages: 0, uptime: 0, clients: 0 });
  useEffect(() => {
    const targets = { messages: 150, uptime: 99.9, clients: 500 };
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = Math.min(step / steps, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setStats({
        messages: Math.round(targets.messages * ease),
        uptime: Math.round(targets.uptime * ease * 10) / 10,
        clients: Math.round(targets.clients * ease),
      });
      if (step >= steps) clearInterval(timer);
    }, interval);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden flex" style={{ background: '#0a0e1a' }}>
      {/* CSS Animations */}
      <style>{`
        @keyframes meshMove1 { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(30px,-50px) scale(1.1)} 66%{transform:translate(-20px,20px) scale(0.9)} }
        @keyframes meshMove2 { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(-40px,30px) scale(1.15)} 66%{transform:translate(25px,-35px) scale(0.95)} }
        @keyframes meshMove3 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(35px,25px) scale(1.05)} }
        @keyframes pulse-ring { 0%{transform:scale(0.8);opacity:0.5} 50%{transform:scale(1);opacity:0.2} 100%{transform:scale(0.8);opacity:0.5} }
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
        @keyframes gridScroll { 0%{transform:translateY(0)} 100%{transform:translateY(40px)} }
        @keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        .mesh-orb-1 { animation: meshMove1 12s ease-in-out infinite; }
        .mesh-orb-2 { animation: meshMove2 15s ease-in-out infinite; }
        .mesh-orb-3 { animation: meshMove3 10s ease-in-out infinite; }
        .pulse-ring { animation: pulse-ring 3s ease-in-out infinite; }
        .float-anim { animation: float 6s ease-in-out infinite; }
        .grid-scroll { animation: gridScroll 4s linear infinite; }
        .shimmer-btn { background-size: 200% 100%; animation: shimmer 3s ease-in-out infinite; }
        .input-glow:focus { box-shadow: 0 0 0 3px rgba(99,102,241,0.15), 0 0 20px rgba(99,102,241,0.1); }
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px rgba(255,255,255,0.1) inset !important;
          -webkit-text-fill-color: #ffffff !important;
          caret-color: #ffffff !important;
          transition: background-color 5000s ease-in-out 0s;
        }
      `}</style>

      {/* Animated Mesh Gradient Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="mesh-orb-1 absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full opacity-30" style={{ background: 'radial-gradient(circle, #6366f1 0%, transparent 70%)' }} />
        <div className="mesh-orb-2 absolute top-1/3 right-0 w-[600px] h-[600px] rounded-full opacity-20" style={{ background: 'radial-gradient(circle, #8b5cf6 0%, transparent 70%)' }} />
        <div className="mesh-orb-3 absolute -bottom-40 left-1/3 w-[500px] h-[500px] rounded-full opacity-25" style={{ background: 'radial-gradient(circle, #06b6d4 0%, transparent 70%)' }} />
        {/* Subtle grid overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '40px 40px' }}>
          <div className="grid-scroll w-full h-[calc(100%+40px)]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        </div>
      </div>

      {/* Left Panel - Branding (hidden on mobile) */}
      <motion.div
        initial={{ opacity: 0, x: -60 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className="hidden lg:flex lg:w-[55%] relative z-10 flex-col justify-between py-12 px-16"
      >
        {/* Top - Logo & Brand */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex items-center gap-3"
          >
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-cyan-400 flex items-center justify-center shadow-lg shadow-indigo-500/25">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white tracking-tight">Way2Connect</h2>
              <p className="text-xs text-indigo-300/70 font-medium tracking-widest uppercase">SMPP Platform</p>
            </div>
          </motion.div>
        </div>

        {/* Center - Hero Content */}
        <div className="flex-1 flex flex-col justify-center -mt-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            <h1 className="text-5xl xl:text-6xl font-extrabold text-white leading-tight mb-6">
              Enterprise
              <br />
              <span className="bg-gradient-to-r from-indigo-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                SMS Gateway
              </span>
            </h1>
            <p className="text-lg text-gray-400 max-w-md leading-relaxed mb-10">
              Deliver millions of messages with carrier-grade reliability. Real-time analytics, DLT compliance, and blazing-fast SMPP connectivity.
            </p>

            {/* Feature Pills */}
            <div className="flex flex-wrap gap-3 mb-12">
              {[
                { icon: '⚡', label: 'High TPS' },
                { icon: '🛡️', label: 'DLT Compliant' },
                { icon: '📊', label: 'Live Analytics' },
                { icon: '🔗', label: 'SMPP v3.4' },
              ].map((pill, i) => (
                <motion.div
                  key={pill.label}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.8 + i * 0.1 }}
                  className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm"
                >
                  <span className="text-sm">{pill.icon}</span>
                  <span className="text-sm text-gray-300 font-medium">{pill.label}</span>
                </motion.div>
              ))}
            </div>

            {/* Stats Row */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 }}
              className="flex gap-10"
            >
              <div>
                <p className="text-3xl font-bold text-white">{stats.messages}M+</p>
                <p className="text-sm text-gray-500 mt-1">Messages/month</p>
              </div>
              <div className="w-px bg-white/10" />
              <div>
                <p className="text-3xl font-bold text-white">{stats.uptime}%</p>
                <p className="text-sm text-gray-500 mt-1">Uptime SLA</p>
              </div>
              <div className="w-px bg-white/10" />
              <div>
                <p className="text-3xl font-bold text-white">{stats.clients}+</p>
                <p className="text-sm text-gray-500 mt-1">Active clients</p>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom - Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="text-sm text-gray-600"
        >
          &copy; {new Date().getFullYear()} WAY2SEND PRIVATE LIMITED. All rights reserved.
        </motion.div>
      </motion.div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-[45%] relative z-10 flex items-center justify-center p-6 sm:p-8">
        {/* Mobile logo (shown only on small screens) */}
        <div className="lg:hidden absolute top-8 left-8 flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-400 flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <span className="text-lg font-bold text-white">Way2Connect</span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.2, ease: 'easeOut' }}
          className="w-full max-w-[420px]"
        >
          {/* Glassmorphism Card */}
          <div 
            className="rounded-3xl p-8 sm:p-10 border border-white/10 shadow-2xl"
            style={{ 
              background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)',
              backdropFilter: 'blur(40px)',
              WebkitBackdropFilter: 'blur(40px)',
            }}
          >
            <AnimatePresence mode="wait">
            {mobileSelectMode ? (
              <motion.div key="mobile-select" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.4 }}>
                {/* Mobile Selection Header */}
                <div className="text-center mb-8">
                  <div className="relative inline-block mb-5">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-400 flex items-center justify-center shadow-xl shadow-violet-500/30">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="pulse-ring absolute inset-0 rounded-2xl border-2 border-violet-400/30" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-1">Select Mobile Number</h2>
                  <p className="text-sm text-gray-400">Choose a number to receive your verification code</p>
                </div>

                {/* Error */}
                <AnimatePresence>
                  {error && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl border border-red-500/20 bg-red-500/10 mb-6">
                      <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </div>
                      <span className="text-sm text-red-300">{error}</span>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Mobile Options */}
                <div className="space-y-3 mb-6">
                  {availableMobiles.map((mob, i) => (
                    <motion.button
                      key={i}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + i * 0.08 }}
                      onClick={() => handleMobileSelect(mob.value)}
                      disabled={loading}
                      className="w-full flex items-center gap-4 p-4 rounded-xl border border-white/10 hover:border-violet-500/40 hover:bg-violet-500/5 transition-all duration-200 group disabled:opacity-50"
                      style={{ backgroundColor: 'rgba(15, 20, 35, 0.6)' }}
                    >
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500/20 to-indigo-500/20 flex items-center justify-center group-hover:from-violet-500/30 group-hover:to-indigo-500/30 transition-all">
                        <svg className="w-5 h-5 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div className="flex-1 text-left">
                        <div className="text-white font-semibold text-sm tracking-wider">{mob.masked}</div>
                        <div className="text-xs text-gray-500">Tap to send OTP</div>
                      </div>
                      <svg className="w-5 h-5 text-gray-500 group-hover:text-violet-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </motion.button>
                  ))}
                </div>

                {/* Loading indicator */}
                {loading && (
                  <div className="flex items-center justify-center gap-2 mb-4 text-violet-400 text-sm">
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                    Sending OTP...
                  </div>
                )}

                {/* Back to login */}
                <div className="text-center">
                  <button onClick={handleBackToLogin} className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-1 mx-auto">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                    Back to login
                  </button>
                </div>
              </motion.div>
            ) : !otpMode ? (
              <motion.div key="login-form" initial={{ opacity: 1 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }}>
            {/* Header */}
            <div className="text-center mb-8">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.4, type: 'spring', stiffness: 200, damping: 15 }}
                className="relative inline-block mb-5"
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-cyan-400 flex items-center justify-center shadow-xl shadow-indigo-500/30">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div className="pulse-ring absolute inset-0 rounded-2xl border-2 border-indigo-400/30" />
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="text-2xl font-bold text-white mb-1"
              >
                Welcome back
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="text-sm text-gray-400"
              >
                Sign in to your account to continue
              </motion.p>
            </div>

            {/* Form */}
            <form className="space-y-5" onSubmit={handleSubmit}>
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: 'auto' }}
                    exit={{ opacity: 0, y: -10, height: 0 }}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl border border-red-500/20 bg-red-500/10"
                  >
                    <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <span className="text-sm text-red-300">{error}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Username Field */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 }}
              >
                <label htmlFor="username" className="block text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider">
                  Username
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg className={`w-5 h-5 transition-colors duration-200 ${focusedField === 'username' ? 'text-indigo-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    required
                    className="input-glow block w-full pl-12 pr-4 py-3.5 rounded-xl border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500/50 transition-all duration-200 text-sm"
                    style={{ color: '#ffffff', WebkitTextFillColor: '#ffffff', backgroundColor: 'rgba(15, 20, 35, 0.8)' }}
                    placeholder="Enter your username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    onFocus={() => setFocusedField('username')}
                    onBlur={() => setFocusedField(null)}
                  />
                </div>
              </motion.div>

              {/* Password Field */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 }}
              >
                <label htmlFor="password" className="block text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider">
                  Password
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg className={`w-5 h-5 transition-colors duration-200 ${focusedField === 'password' ? 'text-indigo-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    className="input-glow block w-full pl-12 pr-12 py-3.5 rounded-xl border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500/50 transition-all duration-200 text-sm"
                    style={{ color: '#ffffff', WebkitTextFillColor: '#ffffff', backgroundColor: 'rgba(15, 20, 35, 0.8)' }}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => setFocusedField(null)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-500 hover:text-gray-300 transition-colors"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="w-5 h-5" />
                    ) : (
                      <EyeIcon className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </motion.div>

              {/* Submit Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                className="pt-2"
              >
                <motion.button
                  whileHover={{ scale: 1.01, boxShadow: '0 20px 40px -12px rgba(99,102,241,0.4)' }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={loading}
                  className="shimmer-btn w-full flex justify-center items-center py-4 px-4 rounded-xl text-white font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40"
                  style={{ 
                    background: loading 
                      ? 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)' 
                      : 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #6366f1 100%)'
                  }}
                >
                  {loading ? (
                    <span className="flex items-center gap-3">
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Signing in...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Sign in
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </span>
                  )}
                </motion.button>
              </motion.div>
            </form>
              </motion.div>
            ) : (
              <motion.div key="otp-form" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}>
                {/* OTP Verification Header */}
                <div className="text-center mb-8">
                  <div className="relative inline-block mb-5">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-cyan-400 flex items-center justify-center shadow-xl shadow-emerald-500/30">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="pulse-ring absolute inset-0 rounded-2xl border-2 border-emerald-400/30" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-1">Verify Your Identity</h2>
                  <p className="text-sm text-gray-400">
                    Enter the 6-digit code sent to <span className="text-cyan-400 font-semibold">{otpMobile}</span>
                  </p>
                </div>

                {/* Error */}
                <AnimatePresence>
                  {error && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl border border-red-500/20 bg-red-500/10 mb-6">
                      <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </div>
                      <span className="text-sm text-red-300">{error}</span>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* OTP Input Boxes */}
                <div className="flex justify-center gap-3 mb-6">
                  {otpDigits.map((digit, i) => (
                    <motion.input
                      key={i}
                      ref={otpRefs[i]}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + i * 0.05 }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={e => handleOtpChange(i, e.target.value)}
                      onKeyDown={e => handleOtpKeyDown(i, e)}
                      onPaste={i === 0 ? handleOtpPaste : undefined}
                      className={`input-glow w-12 h-14 text-center text-xl font-bold rounded-xl border transition-all duration-200 focus:outline-none ${
                        digit ? 'border-cyan-500/50 text-cyan-300' : 'border-white/10 text-white'
                      }`}
                      style={{ backgroundColor: 'rgba(15, 20, 35, 0.8)', WebkitTextFillColor: digit ? '#67e8f9' : '#ffffff' }}
                    />
                  ))}
                </div>

                {/* Timer */}
                <div className="text-center mb-6">
                  {otpTimer > 0 ? (
                    <p className="text-sm text-gray-400">
                      Code expires in <span className="text-cyan-400 font-bold">{formatTimer(otpTimer)}</span>
                    </p>
                  ) : (
                    <p className="text-sm text-amber-400">Code expired. Please resend.</p>
                  )}
                </div>

                {/* Verify Button */}
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleOtpSubmit(otpDigits.join(''))}
                  disabled={loading || otpDigits.some(d => !d)}
                  className="shimmer-btn w-full flex justify-center items-center py-4 px-4 rounded-xl text-white font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 mb-4"
                  style={{ background: loading ? 'linear-gradient(135deg, #047857, #059669)' : 'linear-gradient(135deg, #059669 0%, #06b6d4 50%, #0891b2 100%)' }}
                >
                  {loading ? (
                    <span className="flex items-center gap-3">
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                      Verifying...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                      Verify & Sign In
                    </span>
                  )}
                </motion.button>

                {/* Resend & Back */}
                <div className="flex items-center justify-between">
                  <button onClick={handleBackToLogin} className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                    Back to login
                  </button>
                  <button onClick={handleResendOtp} disabled={resending || otpTimer > 270} className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors disabled:text-gray-600 disabled:cursor-not-allowed flex items-center gap-1">
                    {resending ? (
                      <><svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg> Sending...</>
                    ) : (
                      <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg> Resend OTP</>
                    )}
                  </button>
                </div>
              </motion.div>
            )}
            </AnimatePresence>

            {/* Divider */}
            <div className="mt-8 flex items-center gap-3">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-xs text-gray-500 font-medium">SECURE LOGIN</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
              className="mt-5 flex items-center justify-center gap-5"
            >
              <div className="flex items-center gap-1.5 text-gray-500">
                <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-xs">256-bit SSL</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-500">
                <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-xs">DLT Verified</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-500">
                <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-xs">MFA</span>
              </div>
            </motion.div>
          </div>

          {/* Mobile Footer */}
          <p className="lg:hidden text-center text-xs text-gray-600 mt-6">
            &copy; {new Date().getFullYear()} WAY2SEND PRIVATE LIMITED. All rights reserved.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
